import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check } from "lucide-react";

export const EMPTY_ALERT = { name: "", sectors: [], regions: [], budget_max: "", active: true };

const Chips = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map((o) => {
      const on = value.includes(o);
      return (
        <button key={o} type="button" data-testid={`${testid}-${o}`} onClick={() => onChange(on ? value.filter((x) => x !== o) : [...value, o])}
          className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-[#1B2A4A] text-white border-[#1B2A4A]" : "bg-white text-[#1B2A4A] border-gray-300 hover:border-[#1B2A4A]"}`}>
          {on && <Check className="w-3 h-3" />}{o}
        </button>
      );
    })}
  </div>
);

export default function AlertDialog({ alert, onOpenChange, onSaved }) {
  const [form, setForm] = useState(EMPTY_ALERT);
  const [ref, setRef] = useState({ sectors: [], regions: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/meta/reference").then((r) => setRef(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (alert) setForm({ ...EMPTY_ALERT, ...alert, budget_max: alert.budget_max ?? "" }); }, [alert]);
  const editing = !!alert?.alert_id;

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    const payload = { name: form.name.trim(), sectors: form.sectors, regions: form.regions, active: form.active, budget_max: form.budget_max === "" ? null : Math.round(Number(form.budget_max)) };
    try {
      if (editing) await api.put(`/alerts/${alert.alert_id}`, payload); else await api.post("/alerts", payload);
      toast.success(editing ? "Alerte mise à jour" : "Alerte créée : vous serez prévenu par email"); onSaved(); onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!alert} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none max-h-[92vh] overflow-y-auto" data-testid="alert-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl text-[#1B2A4A]">{editing ? "Modifier l'alerte" : "Nouvelle alerte"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-5">
          <div><Label className="text-xs uppercase tracking-wider text-[#475569]">Nom de l'alerte</Label>
            <Input data-testid="alert-field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex. BTP dans le Var" className="rounded-none mt-1.5 border-gray-300" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-[#475569]">Secteurs <span className="normal-case tracking-normal text-[#94A3B8]">(aucun = tous)</span></Label>
            <div className="mt-2"><Chips testid="alert-sectors" options={ref.sectors} value={form.sectors} onChange={(sectors) => setForm({ ...form, sectors })} /></div></div>
          <div><Label className="text-xs uppercase tracking-wider text-[#475569]">Régions <span className="normal-case tracking-normal text-[#94A3B8]">(aucune = toutes)</span></Label>
            <div className="mt-2"><Chips testid="alert-regions" options={ref.regions} value={form.regions} onChange={(regions) => setForm({ ...form, regions })} /></div></div>
          <div><Label className="text-xs uppercase tracking-wider text-[#475569]">Budget maximum (€)</Label>
            <Input data-testid="alert-field-budget" type="number" min="0" step="any" value={form.budget_max} onChange={(e) => setForm({ ...form, budget_max: e.target.value })} placeholder="Ex. 400000 — vide = sans limite" className="rounded-none mt-1.5 border-gray-300 max-w-xs" /></div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Annuler</Button>
            <Button data-testid="alert-save" type="submit" disabled={busy} className="rounded-full bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold px-6">{busy ? "Enregistrement…" : "Enregistrer l'alerte"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
